/**
 * WebSocket hook for real-time sync updates
 * 
 * Connects to the backend WebSocket endpoint and provides real-time sync operation updates
 */

import { useEffect, useRef, useState, useCallback } from 'react'
import { SyncOperation } from '../services/api'
import { generateWebSocketUrl } from '../utils/urls'

interface WebSocketMessage {
  type: 'sync_update' | 'keepalive' | 'pong'
  timestamp: string
  data?: SyncOperation
}

interface UseSyncWebSocketOptions {
  fpCode?: string // Filter updates for specific franchise partner
  autoReconnect?: boolean
  reconnectInterval?: number
}

interface UseSyncWebSocketReturn {
  isConnected: boolean
  lastUpdate?: SyncOperation
  connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error'
  reconnect: () => void
  disconnect: () => void
}

export const useSyncWebSocket = (
  options: UseSyncWebSocketOptions = {}
): UseSyncWebSocketReturn => {
  const {
    fpCode,
    autoReconnect = true,
    reconnectInterval = 5000
  } = options

  const [isConnected, setIsConnected] = useState(false)
  const [lastUpdate, setLastUpdate] = useState<SyncOperation | undefined>()
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>('disconnected')
  
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const shouldReconnectRef = useRef(true)
  const isCleaningUpRef = useRef(false)

  const getWebSocketUrl = useCallback(async () => {
    const baseUrl = generateWebSocketUrl('/api/sync/live')
    
    const url = new URL(baseUrl)
    if (fpCode) {
      url.searchParams.set('fp_code', fpCode)
    }
    
    // Fetch authentication token from API endpoint with cache prevention
    try {
      const response = await fetch(`/api/auth/token?_t=${Date.now()}`, {
        method: 'GET',
        credentials: 'include', // Include cookies
        cache: 'no-store', // Prevent browser caching
        headers: {
          'Accept': 'application/json',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
        },
      })
      
      if (response.ok) {
        const data = await response.json()
        if (data.token) {
          url.searchParams.set('token', data.token)
          console.log('🔑 Token fetched for WebSocket connection')
        } else {
          console.warn('⚠️ No token in response')
        }
      } else {
        console.warn('⚠️ Failed to fetch token for WebSocket:', response.status)
      }
    } catch (error) {
      console.error('❌ Error fetching token for WebSocket:', error)
    }
    
    return url.toString()
  }, [fpCode])

  const disconnect = useCallback(() => {
    shouldReconnectRef.current = false
    isCleaningUpRef.current = true
    
    // Clear reconnect timeout first to prevent reconnection
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
    
    // Clear ping interval
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current)
      pingIntervalRef.current = null
    }
    
    // Close WebSocket with normal closure code
    if (wsRef.current) {
      const ws = wsRef.current
      wsRef.current = null
      
      // Remove event handlers before closing to prevent reconnection
      ws.onclose = null
      ws.onerror = null
      ws.onmessage = null
      ws.onopen = null
      
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        ws.close(1000, 'Component unmounting')
      }
    }
    
    setIsConnected(false)
    setConnectionStatus('disconnected')
    isCleaningUpRef.current = false
  }, [])

  const sendPing = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'ping' }))
    }
  }, [])

  const connect = useCallback(async () => {
    // Don't connect if cleaning up or already connected
    if (isCleaningUpRef.current || !shouldReconnectRef.current) {
      return
    }
    
    if (wsRef.current?.readyState === WebSocket.OPEN || wsRef.current?.readyState === WebSocket.CONNECTING) {
      return // Already connected or connecting
    }

    setConnectionStatus('connecting')
    
    try {
      const wsUrl = await getWebSocketUrl()
      const ws = new WebSocket(wsUrl)
      wsRef.current = ws

      ws.onopen = () => {
        console.log('🔌 WebSocket connected for sync updates')
        setIsConnected(true)
        setConnectionStatus('connected')
        
        // Start ping interval to keep connection alive
        pingIntervalRef.current = setInterval(sendPing, 25000) // Ping every 25 seconds
      }

      ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data)
          
          switch (message.type) {
            case 'sync_update':
              if (message.data) {
                setLastUpdate(message.data)
                console.log('🔄 Sync update received:', message.data.operation_id, message.data.status)
              }
              break
            case 'keepalive':
              console.log('💓 WebSocket keepalive received')
              break
            case 'pong':
              console.log('🏓 WebSocket pong received')
              break
            default:
              console.log('📨 Unknown WebSocket message type:', message.type)
          }
        } catch (error) {
          console.error('❌ Failed to parse WebSocket message:', error)
        }
      }

      ws.onclose = (event) => {
        console.log('🔌 WebSocket disconnected:', event.code, event.reason)
        setIsConnected(false)
        
        // Clear the reference if this is our current connection
        if (wsRef.current === ws) {
          wsRef.current = null
        }
        
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current)
          pingIntervalRef.current = null
        }

        // Only reconnect if not cleaning up and auto-reconnect is enabled
        if (!isCleaningUpRef.current && shouldReconnectRef.current && autoReconnect && event.code !== 1000 && event.code !== 1008) {
          setConnectionStatus('connecting')
          reconnectTimeoutRef.current = setTimeout(() => {
            if (shouldReconnectRef.current && !isCleaningUpRef.current) {
              connect()
            }
          }, reconnectInterval)
        } else {
          setConnectionStatus('disconnected')
        }
      }

      ws.onerror = (error) => {
        console.error('❌ WebSocket error:', error)
        setConnectionStatus('error')
      }

    } catch (error) {
      console.error('❌ Failed to create WebSocket connection:', error)
      setConnectionStatus('error')
      
      if (autoReconnect && shouldReconnectRef.current) {
        reconnectTimeoutRef.current = setTimeout(connect, reconnectInterval)
      }
    }
  }, [getWebSocketUrl, autoReconnect, reconnectInterval, sendPing])

  const reconnect = useCallback(() => {
    disconnect()
    shouldReconnectRef.current = true
    setTimeout(() => {
      connect()
    }, 100)
  }, [disconnect, connect])

  // Initialize connection on mount
  useEffect(() => {
    shouldReconnectRef.current = true
    isCleaningUpRef.current = false
    connect()

    // Cleanup on unmount
    return () => {
      console.log('🧹 Cleaning up WebSocket connection')
      disconnect()
    }
  }, []) // Remove dependencies to prevent re-runs

  // Reconnect when fpCode changes
  useEffect(() => {
    if (fpCode !== undefined && wsRef.current) {
      console.log('📝 FP code changed, reconnecting WebSocket')
      reconnect()
    }
  }, [fpCode]) // Remove reconnect dependency to prevent loops

  return {
    isConnected,
    lastUpdate,
    connectionStatus,
    reconnect,
    disconnect
  }
}

export default useSyncWebSocket